import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { ArrowsRightLeftIcon, ArchiveBoxIcon, LightBulbIcon, ExclamationTriangleIcon, CommandLineIcon, ArrowPathIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import apiService from '../services/api';
import { OdooModule, OdooModuleAdd, ConnectedRepository, OdooModuleLogEntry } from '../types/api';
import { getApiErrorMessage } from '../constants/messages';
import Logger from '../utils/logger';
import { useTranslation } from 'react-i18next';

interface OdooModulesSectionProps {
  deploymentId: string;
}

const OdooModulesSection: React.FC<OdooModulesSectionProps> = ({ deploymentId }) => {
  const { t } = (useTranslation as any)('deployments');
  const [modules, setModules] = useState<OdooModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMethod, setAddMethod] = useState<'git' | 'zip'>('git');
  const [connectedRepos, setConnectedRepos] = useState<ConnectedRepository[]>([]);

  // Form states pour Git
  const [useConnectedRepo, setUseConnectedRepo] = useState(true);
  const [selectedRepoId, setSelectedRepoId] = useState<number | null>(null);
  const [gitSubpath, setGitSubpath] = useState('');
  const [gitUrl, setGitUrl] = useState('');
  const [gitBranch, setGitBranch] = useState('17.0');
  const [moduleName, setModuleName] = useState('');
  const [autoInstall, setAutoInstall] = useState(false);

  // Form states pour ZIP
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [zipModuleName, setZipModuleName] = useState('');

  const [submitting, setSubmitting] = useState(false);

  // Logs states
  const [showLogs, setShowLogs] = useState(false);
  const [moduleLogs, setModuleLogs] = useState<OdooModuleLogEntry[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedModuleLog, setExpandedModuleLog] = useState<number | null>(null);
  const logsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadModules();
    loadConnectedRepos();
  }, [deploymentId]);

  const loadModules = async () => {
    try {
      setLoading(true);
      const response = await apiService.getOdooModules(deploymentId);
      setModules(response.modules);
    } catch (error: unknown) {
      Logger.error('Error loading modules:', error);
      toast.error('Erreur lors du chargement des modules');
    } finally {
      setLoading(false);
    }
  };

  const loadConnectedRepos = async () => {
    try {
      const repos = await apiService.getConnectedRepositories();
      setConnectedRepos(repos);
      if (repos.length > 0) {
        setSelectedRepoId(repos[0].id);
      }
    } catch (error) {
      Logger.error('Error loading connected repos:', error);
    }
  };

  const resetForm = () => {
    setUseConnectedRepo(true);
    setSelectedRepoId(connectedRepos.length > 0 ? connectedRepos[0].id : null);
    setGitSubpath('');
    setGitUrl('');
    setGitBranch('17.0');
    setModuleName('');
    setAutoInstall(false);
    setZipFile(null);
    setZipModuleName('');
  };

  const handleAddModule = async (e: React.FormEvent) => {
    e.preventDefault();

    if (addMethod === 'git') {
      // Validation pour Git
      if (!moduleName.trim()) {
        toast.error('Le nom du module est requis');
        return;
      }

      if (useConnectedRepo) {
        if (!selectedRepoId) {
          toast.error('Veuillez sélectionner un repository connecté');
          return;
        }
      } else {
        if (!gitUrl.trim()) {
          toast.error('L\'URL Git est requise');
          return;
        }
      }

      try {
        setSubmitting(true);

        const moduleData: OdooModuleAdd = {
          deployment_id: deploymentId,
          module_type: 'git',
          module_name: moduleName.trim(),
          auto_install: autoInstall,
        };

        if (useConnectedRepo) {
          moduleData.connected_repo_id = selectedRepoId!;
          moduleData.git_branch = gitBranch.trim() || '17.0';
          if (gitSubpath.trim()) {
            moduleData.git_subpath = gitSubpath.trim();
          }
        } else {
          moduleData.git_url = gitUrl.trim();
          moduleData.git_branch = gitBranch.trim() || '17.0';
          if (gitSubpath.trim()) {
            moduleData.git_subpath = gitSubpath.trim();
          }
        }

        await apiService.addOdooModule(moduleData);
        toast.success('Module Odoo ajouté avec succès !');
        setShowAddModal(false);
        resetForm();
        loadModules();
      } catch (error: unknown) {
        Logger.error('Error adding module:', error);
        const errorMessage = getApiErrorMessage(error) || 'Erreur lors de l\'ajout du module';
        toast.error(errorMessage);
      } finally {
        setSubmitting(false);
      }
    } else {
      // Validation pour ZIP
      if (!zipModuleName.trim()) {
        toast.error('Le nom du module est requis');
        return;
      }

      if (!zipFile) {
        toast.error('Veuillez sélectionner un fichier ZIP');
        return;
      }

      // Validation de la taille (1 MB max)
      const maxSize = 1 * 1024 * 1024; // 1 MB en bytes
      if (zipFile.size > maxSize) {
        const sizeMB = (zipFile.size / 1024 / 1024).toFixed(2);
        toast.error(`Fichier trop volumineux (${sizeMB} MB). La limite est de 1 MB. Utilisez un repository Git pour les modules plus volumineux.`);
        return;
      }

      try {
        setSubmitting(true);

        await apiService.uploadOdooModuleZip(
          deploymentId,
          zipModuleName.trim(),
          zipFile,
          autoInstall
        );

        toast.success('Module Odoo uploadé avec succès !');
        setShowAddModal(false);
        resetForm();
        loadModules();
      } catch (error: unknown) {
        Logger.error('Error uploading module:', error);
        const errorMessage = getApiErrorMessage(error) || 'Erreur lors de l\'upload du module';
        toast.error(errorMessage);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleDeleteModule = async (moduleId: number, moduleName: string) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le module "${moduleName}" ?`)) {
      return;
    }

    try {
      await apiService.deleteOdooModule(moduleId);
      toast.success('Module supprimé avec succès');
      loadModules();
    } catch (error: unknown) {
      Logger.error('Error deleting module:', error);
      const errorMessage = getApiErrorMessage(error) || 'Erreur lors de la suppression du module';
      toast.error(errorMessage);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.zip')) {
        toast.error('Seuls les fichiers ZIP sont acceptés');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Le fichier ne doit pas dépasser 10 MB');
        return;
      }
      setZipFile(file);
    }
  };

  const loadLogs = async () => {
    try {
      setLogsLoading(true);
      const response = await apiService.getOdooModuleLogs(deploymentId);
      setModuleLogs(response.modules);
      if (response.modules.length > 0) {
        setExpandedModuleLog(response.modules[0].module_id);
      }
    } catch (error: unknown) {
      Logger.error('Error loading module logs:', error);
      toast.error('Erreur lors du chargement des logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleToggleLogs = () => {
    if (!showLogs) {
      loadLogs();
    }
    setShowLogs(!showLogs);
  };

  const parseLogLines = (rawLogs: string | null): string[] => {
    if (!rawLogs) return [];
    // ArgoCD NDJSON: each line is {"result":{"content":"...","podName":"..."}}
    const lines: string[] = [];
    for (const line of rawLogs.split('\n')) {
      if (!line.trim()) continue;
      try {
        const parsed = JSON.parse(line);
        const content = parsed?.result?.content;
        if (content) lines.push(content);
      } catch {
        // Fallback: plain text line
        if (line.trim()) lines.push(line);
      }
    }
    return lines;
  };

  const getLogStatusBadge = (status: string) => {
    switch (status) {
      case 'ok':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500/12 text-green-400">OK</span>;
      case 'no_logs':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/12 text-yellow-400">Pas de logs</span>;
      case 'error':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-500/12 text-red-400">Erreur</span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface-3 text-text-secondary">Inconnu</span>;
    }
  };

  return (
    <div className="bg-surface-2 overflow-hidden shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg leading-6 font-medium text-text-primary">
            Modules Odoo personnalisés
          </h3>
          <div className="flex items-center space-x-2">
            {modules.length > 0 && (
              <button
                onClick={handleToggleLogs}
                className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 ${
                  showLogs
                    ? 'border-border-1 bg-surface-3 text-text-primary'
                    : 'border-border-1 text-text-secondary hover:bg-surface-3'
                }`}
              >
                <CommandLineIcon className="w-4 h-4 mr-2" />
                Logs d'installation
                {showLogs ? <ChevronUpIcon className="w-4 h-4 ml-1" /> : <ChevronDownIcon className="w-4 h-4 ml-1" />}
              </button>
            )}
            <button
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Ajouter un module
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-500"></div>
          </div>
        ) : modules.length === 0 ? (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="mt-2 text-sm text-text-tertiary">Aucun module personnalisé installé</p>
            <p className="text-xs text-text-quaternary mt-1">Ajoutez des modules Odoo depuis Git ou via un fichier ZIP</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border-0">
              <thead className="bg-surface-3">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Nom du module
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Ajouté le
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface-2 divide-y divide-border-0">
                {modules.map((module) => (
                  <tr key={module.id} className="hover:bg-surface-3">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text-primary">
                      {module.module_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-tertiary">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        module.module_type === 'git'
                          ? 'bg-green-500/12 text-green-400'
                          : 'bg-blue-500/12 text-blue-400'
                      }`}>
                        {module.module_type === 'git' ? <><ArrowsRightLeftIcon className="w-4 h-4 inline mr-1" /> Git</> : <><ArchiveBoxIcon className="w-4 h-4 inline mr-1" /> ZIP</>}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-text-tertiary max-w-xs truncate">
                      {module.module_type === 'zip'
                        ? <span className="italic text-text-quaternary">N/A (Module ZIP)</span>
                        : (module.git_url || 'Repository connecté')
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/12 text-green-400">
                        <CheckCircleIcon className="w-4 h-4 inline mr-1" /> Déployé
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-tertiary">
                      {new Date(module.created_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleDeleteModule(module.id, module.module_name)}
                        className="text-red-400 hover:text-red-300"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Section Logs d'installation */}
        {showLogs && (
          <div className="mt-4 border-t border-border-0 pt-4">
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm font-medium text-text-secondary">Logs d'installation</span>
              <button
                onClick={loadLogs}
                disabled={logsLoading}
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-text-tertiary hover:text-text-primary"
              >
                <ArrowPathIcon className={`w-4 h-4 mr-1 ${logsLoading ? 'animate-spin' : ''}`} />
                Rafraichir
              </button>
            </div>

            {logsLoading ? (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent-500"></div>
              </div>
            ) : moduleLogs.length === 0 ? (
              <p className="text-sm text-text-tertiary text-center py-4">
                Aucun log disponible
              </p>
            ) : (
              <div className="space-y-2">
                {moduleLogs.map((moduleLog) => {
                  const isExpanded = expandedModuleLog === moduleLog.module_id;
                  const logLines = parseLogLines(moduleLog.logs);
                  return (
                    <div key={moduleLog.module_id} className="border border-border-0 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedModuleLog(isExpanded ? null : moduleLog.module_id)}
                        className="w-full flex items-center justify-between px-4 py-2 bg-surface-3 hover:bg-surface-3 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-sm font-medium text-text-primary">{moduleLog.module_name}</span>
                          <span className="text-xs text-text-tertiary">({moduleLog.module_type})</span>
                          {getLogStatusBadge(moduleLog.status)}
                        </div>
                        {isExpanded ? <ChevronUpIcon className="w-4 h-4 text-text-quaternary" /> : <ChevronDownIcon className="w-4 h-4 text-text-quaternary" />}
                      </button>
                      {isExpanded && (
                        <div
                          ref={logsContainerRef}
                          className="bg-gray-900 p-4 font-mono text-xs max-h-64 overflow-y-auto"
                        >
                          {logLines.length === 0 ? (
                            <p className="text-text-quaternary text-center py-4">
                              {moduleLog.status === 'error'
                                ? `Erreur: ${moduleLog.error || 'impossible de recuperer les logs'}`
                                : 'Aucun log disponible. Le pod a peut-etre ete recree depuis la derniere installation.'}
                            </p>
                          ) : (
                            logLines.map((line, idx) => (
                              <div key={idx} className="py-0.5">
                                <span className={
                                  line.startsWith('[OK]') ? 'text-green-400' :
                                  line.startsWith('[ERREUR]') ? 'text-red-400' :
                                  line.startsWith('[AVERTISSEMENT]') ? 'text-yellow-400' :
                                  line.startsWith('[ETAPE') ? 'text-blue-400' :
                                  line.startsWith('[INFO]') ? 'text-cyan-400' :
                                  line.startsWith('==========') ? 'text-white font-bold' :
                                  'text-green-400'
                                }>{line}</span>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal d'ajout */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-border-0 w-full max-w-2xl shadow-lg rounded-md bg-surface-2">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-text-primary">
                Ajouter un module Odoo
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-text-quaternary hover:text-text-secondary"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Tabs pour choisir la méthode */}
            <div className="mb-6 border-b border-border-0">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setAddMethod('git')}
                  className={`${
                    addMethod === 'git'
                      ? 'border-accent-500 text-accent-400'
                      : 'border-transparent text-text-tertiary hover:text-text-secondary hover:border-border-1'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  <ArrowsRightLeftIcon className="w-4 h-4 inline mr-1" /> Repository Git
                </button>
                <button
                  onClick={() => setAddMethod('zip')}
                  className={`${
                    addMethod === 'zip'
                      ? 'border-accent-500 text-accent-400'
                      : 'border-transparent text-text-tertiary hover:text-text-secondary hover:border-border-1'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  <ArchiveBoxIcon className="w-4 h-4 inline mr-1" /> Upload ZIP
                </button>
              </nav>
            </div>

            <form onSubmit={handleAddModule}>
              {addMethod === 'git' ? (
                <div className="space-y-4">
                  {/* Info box */}
                  <div className="p-3 bg-blue-500/12 rounded-lg border border-blue-500/20">
                    <p className="text-sm text-blue-400">
                      <LightBulbIcon className="w-4 h-4 inline mr-1" /> Ajoutez des modules Odoo depuis un repository Git. Idéal pour les modules OCA ou vos propres modules.
                    </p>
                  </div>

                  {/* Choix: Repository connecté ou URL publique */}
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-text-secondary mb-3">
                      Type de repository
                    </label>

                    {/* Options horizontales */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Option 1: Repository connecté (privé) */}
                      <div
                        onClick={() => setUseConnectedRepo(true)}
                        className={`relative cursor-pointer rounded-lg border-2 p-4 transition-all ${
                          useConnectedRepo
                            ? 'border-accent-500 bg-accent-500/12'
                            : 'border-border-1 hover:border-border-0'
                        }`}
                      >
                        <div className="flex flex-col items-center text-center">
                          <input
                            type="radio"
                            checked={useConnectedRepo}
                            onChange={() => setUseConnectedRepo(true)}
                            className="mb-3 h-4 w-4 text-accent-600"
                          />
                          <span className="text-base font-semibold text-text-primary mb-2">
                            Repository connecté
                          </span>
                          <div className="flex flex-wrap justify-center gap-2 mb-2">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500/12 text-green-400">
                              Recommandé
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-500/12 text-purple-400">
                              Privé
                            </span>
                          </div>
                          <p className="text-xs text-text-tertiary">
                            Supporte les repositories privés et publics via OAuth
                          </p>
                        </div>
                      </div>

                      {/* Option 2: URL Git publique */}
                      <div
                        onClick={() => setUseConnectedRepo(false)}
                        className={`relative cursor-pointer rounded-lg border-2 p-4 transition-all ${
                          !useConnectedRepo
                            ? 'border-accent-500 bg-accent-500/12'
                            : 'border-border-1 hover:border-border-0'
                        }`}
                      >
                        <div className="flex flex-col items-center text-center">
                          <input
                            type="radio"
                            checked={!useConnectedRepo}
                            onChange={() => setUseConnectedRepo(false)}
                            className="mb-3 h-4 w-4 text-accent-600"
                          />
                          <span className="text-base font-semibold text-text-primary mb-2">
                            URL Git publique
                          </span>
                          <div className="flex justify-center gap-2 mb-2">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/12 text-blue-400">
                              Public uniquement
                            </span>
                          </div>
                          <p className="text-xs text-text-tertiary">
                            Pour modules OCA ou repositories publics GitHub
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Champs pour repository connecté */}
                    {useConnectedRepo && (
                      <div className="space-y-3 p-4 bg-surface-3 rounded-lg border border-border-0">
                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            Repository connecté
                          </label>
                          <select
                            value={selectedRepoId || ''}
                            onChange={(e) => setSelectedRepoId(Number(e.target.value))}
                            className="w-full px-3 py-2 border border-border-1 rounded-md bg-surface-2 text-text-primary"
                            required={useConnectedRepo}
                          >
                            <option value="">Sélectionnez un repository</option>
                            {connectedRepos.map((repo) => (
                              <option key={repo.id} value={repo.id}>
                                {repo.repo_owner}/{repo.repo_name}
                              </option>
                            ))}
                          </select>
                          {connectedRepos.length === 0 && (
                            <p className="mt-1 text-xs text-orange-400">
                              Aucun repository connecté. Connectez-en un depuis la page Builds.
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            Branche Git
                          </label>
                          <input
                            type="text"
                            value={gitBranch}
                            onChange={(e) => setGitBranch(e.target.value)}
                            className="w-full px-3 py-2 border border-border-1 rounded-md bg-surface-2 text-text-primary"
                            placeholder="17.0"
                            required={useConnectedRepo}
                          />
                          <p className="mt-1 text-xs text-text-tertiary">
                            La branche du repository à cloner (ex: 17.0, 18.0, master)
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            Sous-chemin (optionnel)
                          </label>
                          <input
                            type="text"
                            value={gitSubpath}
                            onChange={(e) => setGitSubpath(e.target.value)}
                            className="w-full px-3 py-2 border border-border-1 rounded-md bg-surface-2 text-text-primary"
                            placeholder="addons/my_module"
                          />
                          <p className="mt-1 text-xs text-text-tertiary">
                            Si le module est dans un sous-dossier du repository
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Champs pour URL publique */}
                    {!useConnectedRepo && (
                      <div className="space-y-3 p-4 bg-surface-3 rounded-lg border border-border-0">
                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            URL Git
                          </label>
                          <input
                            type="url"
                            value={gitUrl}
                            onChange={(e) => setGitUrl(e.target.value)}
                            className="w-full px-3 py-2 border border-border-1 rounded-md bg-surface-2 text-text-primary"
                            placeholder="https://github.com/OCA/web.git"
                            required={!useConnectedRepo}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            Branche Git
                          </label>
                          <input
                            type="text"
                            value={gitBranch}
                            onChange={(e) => setGitBranch(e.target.value)}
                            className="w-full px-3 py-2 border border-border-1 rounded-md bg-surface-2 text-text-primary"
                            placeholder="17.0"
                            required={!useConnectedRepo}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            Sous-chemin (optionnel)
                          </label>
                          <input
                            type="text"
                            value={gitSubpath}
                            onChange={(e) => setGitSubpath(e.target.value)}
                            className="w-full px-3 py-2 border border-border-1 rounded-md bg-surface-2 text-text-primary"
                            placeholder="addons/my_module"
                          />
                          <p className="mt-1 text-xs text-text-tertiary">
                            Si le module est dans un sous-dossier du repository
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Nom du module <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={moduleName}
                      onChange={(e) => setModuleName(e.target.value)}
                      className="w-full px-3 py-2 border border-border-1 rounded-md bg-surface-2 text-text-primary"
                      placeholder="web_responsive"
                      required
                    />
                    <p className="mt-1 text-xs text-text-tertiary">
                      Le nom technique du module Odoo
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Info box */}
                  <div className="p-3 bg-orange-500/12 rounded-lg border border-orange-500/20">
                    <p className="text-sm text-orange-400">
                      <ExclamationTriangleIcon className="w-4 h-4 inline mr-1" /> L'upload ZIP stocke le fichier en base de données. Le déploiement automatique n'est pas encore implémenté pour les ZIPs.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Nom du module <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={zipModuleName}
                      onChange={(e) => setZipModuleName(e.target.value)}
                      className="w-full px-3 py-2 border border-border-1 rounded-md bg-surface-2 text-text-primary"
                      placeholder="my_custom_module"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Fichier ZIP <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      accept=".zip"
                      onChange={handleFileChange}
                      className="w-full px-3 py-2 border border-border-1 rounded-md bg-surface-2 text-text-primary"
                      required
                    />
                    <p className="mt-1 text-xs text-text-tertiary">
                      <ExclamationTriangleIcon className="w-4 h-4 inline mr-1" /> <strong>Limite: 1 MB maximum</strong>. Pour les modules plus volumineux, utilisez un repository Git connecté.
                    </p>
                    {zipFile && (
                      <>
                        {zipFile.size <= 1 * 1024 * 1024 ? (
                          <p className="mt-2 text-sm text-green-400">
                            ✓ Fichier sélectionné: {zipFile.name} ({(zipFile.size / 1024 / 1024).toFixed(2)} MB)
                          </p>
                        ) : (
                          <p className="mt-2 text-sm text-red-400">
                            ✗ Fichier trop volumineux: {zipFile.name} ({(zipFile.size / 1024 / 1024).toFixed(2)} MB) - Max: 1 MB
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-text-secondary bg-surface-3 rounded-md hover:bg-surface-3"
                  disabled={submitting}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-md hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <span className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Ajout en cours...
                    </span>
                  ) : (
                    'Ajouter le module'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OdooModulesSection;
